import { Box, Button, Flex, Icon, Text, useDisclosure } from "@chakra-ui/react";
import { ApiErrorProvider } from "@raiden/library/components/ReactHookForm/ApiError";
import useAuthAs from "@raiden/library/hooks/auth/as";
import { useApi } from "@raiden/library/hooks/useApi";
import { usePreferredYear } from "@raiden/library/hooks/usePreferredYear";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { IoAddOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import Splash from "../../../components/Splash";
import SWRHandler from "../../../components/SWR/SWRHandler";
import HoldersDialogCreate from "../../Holders/DialogCreate";
import StepBottomBar from "../StepBottomBar";
import StepLayout from "../StepLayout";
import Form from "./Form";
import Handler, {
  getWizardHolderFormData,
  WIZARD_HOLDERS_FORM_ID,
} from "./Handler";

/**
 * @typedef {Object} Props
 * @property {() => void} nextStep
 *
 * @param {Props} props
 */
function StepHolders({ nextStep }) {
  const intl = useIntl();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { user } = useAuthAs();

  const preferredYear = usePreferredYear();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Holder").Holder[]>} */
  const { swrResponse: swrResponseHolders } = useApi(
    user
      ? generateApiUri({
          id: "@holders.viewAny",
          query: {
            customer_id: user.id,
            fields: ["holder.id_card", "holder.schoolings"],
          },
        })
      : null,
  );

  function handleSuccess() {
    nextStep();
  }

  return (
    <StepLayout
      title={intl.formatMessage({ defaultMessage: "Comptes adhérents" })}>
      <SWRHandler
        swrResponse={swrResponseHolders}
        shouldRenderWithEmptyData={true}
        wrapper={(children) => <Box p="1rem">{children}</Box>}>
        {(response) => (
          <ApiErrorProvider>
            <Handler
              holders={response.data}
              onSuccess={handleSuccess}
              render={({ fields, append }) => (
                <Box pt="1rem">
                  <Text colorScheme="gray" px="1rem">
                    <FormattedMessage defaultMessage="Les adhérents pourront profiter des services du Centre Social Louise Michel. Vous pourrez les inscrire à des activités, les ajouter aux plannings de services, etc." />
                  </Text>

                  {fields.length > 0 ? (
                    <>
                      <Box p="1rem">
                        <Form
                          initialHolders={response.data}
                          fields={fields}
                          append={append}
                        />
                      </Box>

                      <StepBottomBar>
                        <Button
                          type="submit"
                          form={WIZARD_HOLDERS_FORM_ID}
                          colorScheme="brandSecondary">
                          <FormattedMessage defaultMessage="Enregistrer" />
                        </Button>
                      </StepBottomBar>
                    </>
                  ) : (
                    <>
                      <HoldersDialogCreate
                        isOpen={isOpen}
                        onClose={onClose}
                        onSuccess={({ holder }) => {
                          preferredYear &&
                            append(
                              getWizardHolderFormData({
                                holder,
                                preferredYear,
                              }),
                            );
                        }}
                      />

                      <Box p="1rem">
                        <Splash
                          src={generateFrontPath({
                            id: "internal-assets",
                            parameters: {
                              filePath: "illustrations/empty.svg",
                            },
                            includeBasePath: true,
                          })}
                          title={intl.formatMessage({
                            defaultMessage:
                              "Aucun adhérent n'est lié à votre compte",
                          })}
                          description={intl.formatMessage({
                            defaultMessage:
                              "Les adhérents pourront profiter des services du Centre Social Louise Michel. Vous pourrez les inscrire à des activités, les ajouter aux plannings de services, etc.",
                          })}
                          addon={
                            <Flex justify="center">
                              <Button
                                onClick={onOpen}
                                leftIcon={<Icon as={IoAddOutline} />}
                                colorScheme="brandPrimary">
                                <FormattedMessage defaultMessage="Ajouter un adhérent" />
                              </Button>
                            </Flex>
                          }
                        />
                      </Box>
                    </>
                  )}
                </Box>
              )}
            />
          </ApiErrorProvider>
        )}
      </SWRHandler>
    </StepLayout>
  );
}

export default StepHolders;
